import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { ChartMogulModalWrapper } from './style';
import { TextField } from '@mui/material';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';

import { FormEvent, useEffect, useState } from 'react';
import { useConditionalSnackBar } from 'context/snackbar';
import useConnectChartMogulManager from 'hooks/services/chartMogul/useConnectChartMogulManager';
import { getError } from 'crono-fe-common/utils';
import { useAuth } from 'context/auth';
import useConnectChartMogulUser from 'hooks/services/chartMogul/useConnectChartMogulUser';
import useCRMIntegrationGuard from 'hooks/guards/useCRMIntegrationGuard';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { getLabelForIntegrationType } from 'crono-fe-common/utils/crm-utils';
import { Button } from '@crono-one/design-system';

const integrationLabel = getLabelForIntegrationType(
  IntegrationType.CHART_MOGUL,
);

export type ChartMogulModalProps = {
  close: () => void;
  providedIntegrationType?: IntegrationType | null;
  forceFirstIntegration?: boolean;
};

export const ChartMogulModal = ({
  close,
  forceFirstIntegration,
  providedIntegrationType = null,
}: ChartMogulModalProps) => {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState(user?.email ?? '');
  const [apiKey, setApiKey] = useState('');
  const { chartMogulData, isErrorCM } = useCRMIntegrationGuard(
    providedIntegrationType,
  );

  const isFirstIntegration =
    forceFirstIntegration ||
    (chartMogulData && !chartMogulData?.data.data) ||
    isErrorCM;
  const {
    mutate: integrateWithChartMogul,
    error: integrateWithChartMogulError,
    isLoading: isIntegratingWithChartMogul,
    isSuccess: isIntegratingWithChartMogulSuccess,
  } = useConnectChartMogulManager();

  const {
    mutate: connectChartMogul,
    error: connectChartMogulError,
    isLoading: isConnectingChartMogul,
    isSuccess: isConnectingChartMogulSuccess,
  } = useConnectChartMogulUser();

  const isLoading = isIntegratingWithChartMogul || isConnectingChartMogul;
  const isSuccess =
    isIntegratingWithChartMogulSuccess || isConnectingChartMogulSuccess;
  useEffect(() => {
    if (isSuccess) {
      close();
      void refreshUser();
    }
  }, [isSuccess]);

  useConditionalSnackBar([
    {
      condition: !!integrateWithChartMogulError,
      message:
        getError(integrateWithChartMogulError) ??
        `Error integrating with ${integrationLabel}`,
      severity: 'error',
    },
    {
      condition: !!connectChartMogulError,
      message:
        getError(connectChartMogulError) ??
        `Error connecting ${integrationLabel}`,
      severity: 'error',
    },
  ]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFirstIntegration) {
      integrateWithChartMogul({ email, apiKey });
    } else {
      connectChartMogul({ email });
    }
  };

  return (
    <BlurBackgroundDivFixed onClick={close}>
      <ChartMogulModalWrapper onClick={(ev) => ev.stopPropagation()}>
        <form style={{ height: '100%' }} onSubmit={handleSubmit}>
          <FlexDiv direction="column" alignItems="stretch" gap="32px">
            <p className="text-2xl leading-8 font-bold">
              Connect {integrationLabel}
            </p>

            <div className="form-control" style={{ flex: 1 }}>
              <label className="text-sm font-semibold leading-5 text-black">
                Email
              </label>
              <TextField
                style={{ width: '100%' }}
                variant="standard"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                placeholder={`Insert ${integrationLabel} email...`}
              />
            </div>

            {isFirstIntegration && (
              <div className="form-control" style={{ flex: 1 }}>
                <label className="text-sm font-semibold leading-5 text-black">
                  {integrationLabel} API
                </label>
                <TextField
                  style={{ width: '100%' }}
                  variant="standard"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.currentTarget.value)}
                  placeholder="Insert API key..."
                />
              </div>
            )}

            <FlexDiv height="fit" justifyContent="end">
              <Button
                variant="outline"
                onClick={close}
                size={'lg'}
                className={'w-[132px]'}
              >
                Cancel
              </Button>
              <Button
                type={'submit'}
                size={'lg'}
                disabled={isLoading}
                className={'w-[132px]'}
              >
                Connect
              </Button>
            </FlexDiv>
          </FlexDiv>
        </form>
      </ChartMogulModalWrapper>
    </BlurBackgroundDivFixed>
  );
};
